import * as React from 'react'

import { Form } from 'lucide-react'

import FormListSelector from '~/client/src/shared/components/FormListSelector/FormListSelector'
import { getSelectFormTitle } from '~/client/src/shared/constants/resourceTypeLabels'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'

import { renderRestrictionChip } from '../WorkflowFieldRestrictionChip'
import WorkflowFieldRestrictionChipArea from '../WorkflowFieldRestrictionChipArea'
import {
  WorkflowFieldRestrictionContext,
  WorkflowFieldRestrictionItem,
} from '../WorkflowFieldRestrictionContext'
import WorkflowFieldRestrictionPanel from '../WorkflowFieldRestrictionPanel'

interface IProps {
  schema: ResourceSchema
  resourceTypeId: string
  allowedTemplateIds: string[]
  onChange: (templateIds: string[]) => void
}

const chipIcon = (
  <span className="tw:flex-none tw:ml-0.5 tw:mr-[5px] tw:size-4">
    <Form className="tw:size-4 tw:text-muted-foreground" />
  </span>
)

const renderChip = (item: WorkflowFieldRestrictionItem): JSX.Element =>
  renderRestrictionChip(item, chipIcon)

const AllowedForms: React.FC<IProps> = ({
  schema,
  resourceTypeId,
  allowedTemplateIds,
  onChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const { templates, loading } = schema

  const liveTemplateIds = React.useMemo(
    () => new Set(templates.map(t => String(t.id))),
    [templates],
  )

  const liveAllowedCount = React.useMemo(() => {
    let n = 0
    for (const id of allowedTemplateIds) if (liveTemplateIds.has(id)) n++
    return n
  }, [allowedTemplateIds, liveTemplateIds])

  const staleAllowedCount = Math.max(
    0,
    allowedTemplateIds.length - liveAllowedCount,
  )

  const isAllSelected =
    allowedTemplateIds.length === 0 ||
    (templates.length > 0 && liveAllowedCount === templates.length)

  const chipItems = React.useMemo<WorkflowFieldRestrictionItem[]>(() => {
    if (isAllSelected || loading) return []
    return allowedTemplateIds.map(id => {
      const template = templates.find(t => String(t.id) === id)
      return {
        key: id,
        label: template?.name || '',
        isDeleted: !template,
      }
    })
  }, [allowedTemplateIds, templates, isAllSelected, loading])

  const handleApply = React.useCallback(
    (ids: string[]) => {
      let liveCount = 0
      for (const id of ids) if (liveTemplateIds.has(id)) liveCount++
      const isAll = templates.length > 0 && liveCount === templates.length
      onChange(isAll ? [] : ids)
      setIsOpen(false)
    },
    [onChange, templates.length, liveTemplateIds],
  )

  const pickerSelectedIds = React.useMemo(() => {
    if (allowedTemplateIds.length > 0) return allowedTemplateIds
    return templates.filter(t => t.id != null).map(t => String(t.id))
  }, [allowedTemplateIds, templates])

  const handleRemove = React.useCallback(
    (id: string) => {
      onChange(allowedTemplateIds.filter(existingId => existingId !== id))
    },
    [allowedTemplateIds, onChange],
  )

  const ctxValue = React.useMemo(
    () => ({
      state: { items: chipItems, isAllSelected },
      actions: { remove: handleRemove },
      meta: { emptyLabel: Localization.translator.anyForm },
    }),
    [chipItems, isAllSelected, handleRemove],
  )

  return (
    <WorkflowFieldRestrictionContext.Provider value={ctxValue}>
      <WorkflowFieldRestrictionPanel
        trigger={
          <WorkflowFieldRestrictionChipArea
            renderItem={renderChip}
            isOpen={isOpen}
          />
        }
        open={isOpen}
        onOpenChange={setIsOpen}
        side="right"
        align="start"
      >
        <FormListSelector
          mode="multi"
          title={getSelectFormTitle(resourceTypeId)}
          resourceTypeId={resourceTypeId}
          selectedIds={pickerSelectedIds}
          onApply={handleApply}
          onClose={() => setIsOpen(false)}
          applyLabel={Localization.translator.save}
          className="tw:w-[400px] tw:h-[480px] tw:bg-background"
          templates={templates}
          loading={loading}
        />
      </WorkflowFieldRestrictionPanel>
      {!loading && !isAllSelected && staleAllowedCount > 0 && (
        <div className="tw:mt-1 tw:text-body-sm tw:text-destructive">
          {Localization.translator.xAllowedFormsHaveBeenDeleted(
            staleAllowedCount,
          )}
        </div>
      )}
    </WorkflowFieldRestrictionContext.Provider>
  )
}

export default AllowedForms
